import { createContext, useContext, useLayoutEffect, useMemo, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"

type LayoutChromeContextValue = {
    hideMainSheet: boolean
    setHideMainSheet: (hide: boolean) => void
    title: string | null
    setTitle: (title: string | null) => void
}

const LayoutChromeContext = createContext<LayoutChromeContextValue | null>(null)

export const MAIN_SHEET_HEADER_ACTIONS_ID = "main-sheet-header-actions"
export const MAIN_SHEET_FOOTER_ID = "main-sheet-footer"

export const useLayoutChrome = () => {
    const context = useContext(LayoutChromeContext)
    if (!context) {
        throw new Error("useLayoutChrome must be used within Layout")
    }
    return context
}

export const useLayoutChromeOptional = () => useContext(LayoutChromeContext)

/**
 * Hide the full-page white content sheet while this page is mounted.
 * The blue gradient frame stays. Use on pages that build their own panels.
 */
export const useHideMainSheet = () => {
    const { setHideMainSheet } = useLayoutChrome()
    useLayoutEffect(() => {
        setHideMainSheet(true)
        return () => setHideMainSheet(false)
    }, [setHideMainSheet])
}

export const useLayoutChromeState = () => {
    const [hideMainSheet, setHideMainSheet] = useState(false)
    const [title, setTitle] = useState<string | null>(null)
    const value = useMemo(
        () => ({ hideMainSheet, setHideMainSheet, title, setTitle }),
        [hideMainSheet, title]
    )
    return { hideMainSheet, title, value }
}

const usePortalTarget = (id: string) => {
    const [target, setTarget] = useState<HTMLElement | null>(null)
    useLayoutEffect(() => {
        const sync = () => {
            const next = document.getElementById(id)
            setTarget((current) => (current === next ? current : next))
        }
        sync()
        // The sheet title and hide flag mount these nodes after the page
        // portals first render, so a one-shot lookup misses them.
        const observer = new MutationObserver(sync)
        observer.observe(document.body, { childList: true, subtree: true })
        return () => observer.disconnect()
    }, [id])
    return target
}

export const MainSheetHeaderActions = ({ children }: { children: ReactNode }) => {
    const target = usePortalTarget(MAIN_SHEET_HEADER_ACTIONS_ID)
    if (!target) return null
    return createPortal(children, target)
}

export const MainSheetFooter = ({ children }: { children: ReactNode }) => {
    const target = usePortalTarget(MAIN_SHEET_FOOTER_ID)
    if (!target) return null
    return createPortal(children, target)
}

export const LayoutChromeProvider = LayoutChromeContext.Provider
